import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Users, Sparkles, Coins, Banknote, Share2, Send, MessageCircle, Mail, Twitter, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { getMyReferral, listMyReferrals } from "@/lib/afritask";

const fmtWhen = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtNaira = (n) => `₦${Number(n || 0).toLocaleString("en-NG")}`;

export default function Referrals() {
  const { me } = useOutletContext();
  const [summary, setSummary] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const canNativeShare = useRef(
    typeof navigator !== "undefined" && typeof navigator.share === "function"
  );

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        getMyReferral().catch(() => null),
        listMyReferrals().catch(() => []),
      ]);
      setSummary(s);
      setReferrals(r);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (me) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.user_id]);

  if (!summary) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-neutral-500">
          Loading your referral hub…
        </CardContent>
      </Card>
    );
  }

  const shareText =
    `Join me on AfriTask AI — train AI, earn ₦ to your bank. Use my code: ${summary.share_url}`;
  const shareTextEncoded = encodeURIComponent(shareText);

  const handleShareInvite = async () => {
    if (canNativeShare.current) {
      try {
        await navigator.share({
          title: "AfriTask AI",
          text: "Join me on AfriTask AI — train AI, earn ₦ to your bank.",
          url: summary.share_url,
        });
        return;
      } catch {
        // User cancelled OR platform bailed — open the fallback popover.
      }
    }
    setFallbackOpen(true);
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(summary.code);
      toast.success("Code copied");
    } catch {
      toast.error("Copy failed — long-press to copy");
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* 1. Heading (no eyebrow) */}
      <h1 className="text-2xl font-bold tracking-tight">Referrals</h1>

      {/* 2. Hero card — tighter padding, no inline copy icon */}
      <Card className="overflow-hidden border-none shadow-md">
        <div
          className="p-4 text-white sm:p-5"
          style={{ background: "linear-gradient(135deg, #B45309 0%, #D97706 55%, #F59E0B 130%)" }}
        >
          <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-100">
            Your referral code
          </p>
          <p
            className="mt-2 inline-block rounded-lg bg-white/15 px-4 py-2 font-mono text-3xl font-bold tracking-widest text-white"
            data-testid="referral-code"
          >
            {summary.code}
          </p>
          <p
            className="mt-2 truncate font-mono text-xs text-amber-100"
            data-testid="referral-share-url"
          >
            {summary.share_url}
          </p>
        </div>
      </Card>

      {/* 3. Action row — Share invite + Copy code side-by-side */}
      <div className="grid max-w-md grid-cols-2 gap-3">
        <Popover open={fallbackOpen} onOpenChange={setFallbackOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={handleShareInvite}
              data-testid="share-invite-btn"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#B45309] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#9A3412] active:translate-y-px"
            >
              <Share2 className="h-4 w-4" /> Share invite
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-64 space-y-2"
            data-testid="share-fallback-popover"
          >
            <p className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">
              Share via
            </p>
            <div className="grid grid-cols-2 gap-2">
              <FallbackShareBtn
                href={`https://wa.me/?text=${shareTextEncoded}`}
                icon={MessageCircle} label="WhatsApp" testid="share-whatsapp"
              />
              <FallbackShareBtn
                href={`https://t.me/share/url?url=${encodeURIComponent(summary.share_url)}&text=${shareTextEncoded}`}
                icon={Send} label="Telegram" testid="share-telegram"
              />
              <FallbackShareBtn
                href={`https://twitter.com/intent/tweet?text=${shareTextEncoded}`}
                icon={Twitter} label="X" testid="share-twitter"
              />
              <FallbackShareBtn
                href={`mailto:?subject=Join%20me%20on%20AfriTask%20AI&body=${shareTextEncoded}`}
                icon={Mail} label="Email" testid="share-email"
              />
            </div>
          </PopoverContent>
        </Popover>
        <button
          type="button"
          onClick={handleCopyCode}
          data-testid="copy-code-btn"
          className="inline-flex h-11 items-center justify-center rounded-lg border border-neutral-300 bg-white px-4 text-sm font-semibold text-neutral-800 transition hover:bg-neutral-50"
        >
          Copy code
        </button>
      </div>

      {/* 4. Rewards explainer — single card, 2 grouped rows with hairline divider */}
      <Card
        className="divide-y divide-neutral-100 shadow-sm"
        data-testid="referral-how-card"
      >
        <div className="flex items-start gap-3 p-4">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
            <Coins className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-neutral-900">10 points</p>
            <p className="mt-0.5 text-xs text-neutral-500">
              When your friend tops up ≥ ₦1,000
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 p-4">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
            <Banknote className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-neutral-900">5% of their earnings</p>
            <p className="mt-0.5 text-xs text-neutral-500">
              For their first 30 days, paid to your Earnings wallet
            </p>
          </div>
        </div>
      </Card>

      {/* 5. Stats — 4 compact chips */}
      <div
        className="grid grid-cols-4 gap-2 sm:gap-3"
        data-testid="referral-stats-row"
      >
        <StatChip icon={Users} label="Invited"
                  value={String(summary.total_invited)} testid="stat-total-invited" />
        <StatChip icon={Sparkles} label="Activated"
                  value={String(summary.activated)} testid="stat-activated" />
        <StatChip icon={Coins} label="Bonus pts"
                  value={summary.points_earned.toLocaleString()} testid="stat-points-earned" />
        <StatChip icon={Banknote} label="Rev. share" tone="emerald"
                  value={fmtNaira(summary.revenue_share_earned_ngn)}
                  testid="stat-revenue-share" />
      </div>

      {/* 6. Your referrals — compact empty state OR full list */}
      <section>
        <h2 className="mb-3 text-base font-semibold">Your referrals</h2>
        {referrals.length === 0 ? (
          <p
            className="rounded-lg bg-neutral-50 px-4 py-3 text-center text-sm text-neutral-500"
            data-testid="referrals-empty"
          >
            No referrals yet. Share your link to get started.
          </p>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table data-testid="referrals-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Joined</TableHead>
                    <TableHead>Friend</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Bonus pts</TableHead>
                    <TableHead className="text-right">Revenue share</TableHead>
                    <TableHead className="whitespace-nowrap text-right">30-day window</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.map((r) => {
                    const active = r.revenue_share_active;
                    const daysLeft = r.days_remaining;
                    const statusMeta = r.flagged
                      ? { label: "Flagged", tone: "bg-red-50 text-red-700 border-red-200" }
                      : !r.topup_bonus_awarded
                        ? { label: "Registered", tone: "bg-neutral-100 text-neutral-700 border-neutral-200" }
                        : active
                          ? { label: "Revenue-share active", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" }
                          : { label: "Revenue-share expired", tone: "bg-neutral-100 text-neutral-500 border-neutral-200" };
                    return (
                      <TableRow key={r.referral_id} data-testid={`referral-row-${r.referral_id}`}>
                        <TableCell className="whitespace-nowrap text-xs text-neutral-500">
                          {fmtWhen(r.registered_at)}
                        </TableCell>
                        <TableCell className="text-sm font-medium">{r.friend_display_name || r.referee_masked}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusMeta.tone}>{statusMeta.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums text-amber-700">
                          {r.topup_bonus_points > 0 ? `+${r.topup_bonus_points}` : "—"}
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums text-emerald-700">
                          {r.revenue_share_earned_ngn > 0 ? fmtNaira(r.revenue_share_earned_ngn) : "—"}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {active ? (
                            <span className="inline-flex items-center gap-1 text-neutral-600" data-testid={`days-left-${r.referral_id}`}>
                              <Clock className="h-3 w-3" /> {daysLeft} day{daysLeft === 1 ? "" : "s"} left
                            </span>
                          ) : (
                            <span className="text-neutral-400">Expired</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
}

function FallbackShareBtn({ href, icon: Icon, label, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-neutral-200 bg-white text-xs font-medium text-neutral-800 transition hover:bg-neutral-50"
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </a>
  );
}

function StatChip({ icon: Icon, label, value, tone, testid }) {
  const iconTone = tone === "emerald" ? "text-emerald-600" : "text-amber-700";
  const valueTone = tone === "emerald" ? "text-emerald-700" : "text-neutral-900";
  return (
    <Card
      className="rounded-xl border border-amber-300 bg-white shadow-sm ring-1 ring-amber-200/70 transition hover:border-amber-400 hover:ring-amber-300 hover:shadow-md"
      data-testid={testid}
    >
      <CardContent className="p-3">
        <Icon className={`h-4 w-4 ${iconTone}`} />
        <p className="mt-1.5 truncate text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          {label}
        </p>
        <p className={`mt-0.5 truncate text-base font-semibold tabular-nums ${valueTone}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}
